import { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '@urbanx/agx.auth.client';
import { MenuTitles, Pages } from 'constants/menu';
import { useSelectedAgency, useSelectedCampaign } from 'hooks';
import { ContentLayout, PageLayout } from 'layout';
import { AgxToastState } from 'types/commonTypes';
import ContractSection from './ContractSection';
import ContractUploadModal from './ContractUploadModal';
import useContractSectionState from './useContractSectionState';
import useContractModalState from './useContractModalState';
import PropertyInformationSubPage from '../PropertyInformationSubPage';
import { useNavigate } from 'react-router-dom';
import { cleanFullAddress } from '@urbanx/agx.ui-components2';
import { CampaignDetailModelState } from '@urbanx/agx.campaigns.client';
import { AgxToast } from '@urbanx/agx-ui-components';

const ContractPage = () => {
  const navigate = useNavigate();
  const { userAccount } = useContext(AuthContext);
  const { agency } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });
  const {
    saveState,
    hasUnsavedChanges,
    handleSaveChanges,
    pendingDelete,
    contractDocument,
    setPendingDelete,
    isFinalEnabled,
    isFinalSelected,
    setIsFinalSelected,
    isPending,
  } = useContractSectionState({
    campaign: campaign,
    updateToastState,
  });
  const { setIsModalOpen, isModalOpen, showTenancyAgreement, handleModalComplete } =
    useContractModalState({
      campaign,
      updateToastState,
    });

  const isNswCampaign = useMemo(
    () => campaign?.state === CampaignDetailModelState.NSW,
    [campaign?.state]
  );

  useEffect(() => {
    if (campaign && !isNswCampaign && agency?.id) {
      navigate(`/agencies/${agency.id}/campaigns/${campaign.campaignId}/property-information`);
    }
  }, [agency?.id, campaign?.campaignId, isNswCampaign, navigate]);

  if (campaign && !isNswCampaign) {
    return null;
  }

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.PROPERTY_INFORMATION}
        isCampaigns={true}
      >
        <AgxToast selector="#agxToast" toastState={toastState} />
        <PropertyInformationSubPage
          currentPage={Pages.PROPERTY_INFORMATION_CONTRACT}
          isLoading={isPending}
          saveState={saveState}
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleSaveChanges}
          saveButtonId="contractSaveChanges"
          saveButtonTestId="btn-contractSaveChanges"
          showChildren={!isPending}
        >
          <ContractSection
            key={`ContractSection-${pendingDelete}`}
            campaignId={campaign?.campaignId}
            contractDocument={contractDocument}
            pendingDelete={pendingDelete}
            setPendingDelete={setPendingDelete}
            isFinalEnabled={isFinalEnabled}
            isFinalSelected={isFinalSelected}
            setIsFinalSelected={setIsFinalSelected}
            setIsModalOpen={setIsModalOpen}
          />
          {isModalOpen && (
            <ContractUploadModal
              isOpen={isModalOpen}
              showTenancyAgreement={showTenancyAgreement}
              onClose={() => setIsModalOpen(false)}
              onComplete={handleModalComplete}
            />
          )}
        </PropertyInformationSubPage>
      </ContentLayout>
    </PageLayout>
  );
};

export default ContractPage;
